import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  useSortBy,
  usePagination,
} from "react-table";
import { FadeLoader } from "react-spinners";
import { ReactComponent as BagIcon } from "images/svg/bag.svg";
import { ReactComponent as SearchIcon } from "images/svg/search.svg";
import { ReactComponent as FilterIcon } from "images/svg/filter.svg";
import { ReactComponent as GridViewIcon } from "images/svg/grid.svg";
import { ReactComponent as ChartViewIcon } from "images/svg/chart.svg";
import { ReactComponent as CalendarIcon } from "images/svg/calendar-l.svg";
import { ReactComponent as ResetIcon } from "images/svg/reset.svg";
import { COLUMNS } from "./helpers/LeaderboardColumns";
import LeaderboardFilterModal from "views/fellows/pages/Leaderboard/components/LeaderBoardFilterModal";
import FeatureUnavailableModal from "views/fellows/pages/Leaderboard/components/FeatureUnavailable";
import { useNavigate, useSearchParams } from 'react-router-dom'; 

const PublicLeaderBoardTable = ({
  leaderboardData,
  metadata,
  auth_user_row,
  isLoading,
  setFilterString,
  setActiveRecord
}) => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");
  const [filter, setFilter] = useState("");
  const [isCurrentUserFixed, setIsCurrentUserFixed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // const pageSize = metadata?.page_size || 3;
  const pageSize =  3;



  const [isDropdownVisible] = useState(false);
  const gridIconRef = useRef(null);

  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const filterIconRef = useRef(null);
  const chartIconRef = useRef(null);

  const [isUnavailableModalVisible, setIsUnavailableModalVisible] = useState(false);
  const currentFilterRef = useRef({ page: 1, filter: "", searchTerm: "" });

  const currentUserId = auth_user_row?.id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isInitialMount = useRef(true);

  const buildFilterString = useCallback((page, currentFilter, searchTermParam) => {
    let filterParams = `page=${page}&page_size=${pageSize}`;

    if (searchTermParam) {
      filterParams += `&search=${encodeURIComponent(searchTermParam)}`;
    }

    if (currentFilter) {
      if (currentFilter.category) {
        filterParams += `&category=${currentFilter.category}`;
      }
      if (currentFilter.gender) {
        filterParams += `&gender=${currentFilter.gender}`;
      }
      if (currentFilter.state?.length > 0) {
        filterParams += `&state=${currentFilter.state.map(s => s.value).join(',')}`;
      }
      if (currentFilter.lga?.length > 0) {
        filterParams += `&lga=${currentFilter.lga.map(l => l.value).join(',')}`;
      }
      if (currentFilter.course?.length > 0) {
        filterParams += `&course=${currentFilter.course.map(c => c.value).join(',')}`;
      }
      if (currentFilter.min_score) {
        filterParams += `&min_score=${currentFilter.min_score}`;
      }
      if (currentFilter.max_score) {
        filterParams += `&max_score=${currentFilter.max_score}`;
      }
    }

    return filterParams;
  }, [pageSize]);

  const updateURL = useCallback((page, currentFilter, searchTermParam) => {
    const newFilterString = buildFilterString(page, currentFilter, searchTermParam);
    navigate(`?${newFilterString}`, { replace: true });
  }, [buildFilterString, navigate]);

  const updateFilters = useCallback((page = currentPage, currentFilter = filter, searchTermParam = currentSearchTerm) => {
    if (
      page === currentFilterRef.current.page &&
      JSON.stringify(currentFilter) === JSON.stringify(currentFilterRef.current.filter) &&
      searchTermParam === currentFilterRef.current.searchTerm
    ) {
      return;
    }

    currentFilterRef.current = {
      page,
      filter: currentFilter,
      searchTerm: searchTermParam
    };

    updateURL(page, currentFilter, searchTermParam);
    setFilterString(buildFilterString(page, currentFilter, searchTermParam));
  }, [currentPage, filter, currentSearchTerm, buildFilterString, setFilterString, updateURL]);

  useEffect(() => {
    const pageFromURL = searchParams.get('page');
    const searchFromURL = searchParams.get('search');
    const categoryFromURL = searchParams.get('category');
    const genderFromURL = searchParams.get('gender');
    const stateFromURL = searchParams.get('state')?.split(',');
    const lgaFromURL = searchParams.get('lga')?.split(',');
    const courseFromURL = searchParams.get('course')?.split(',');
    const minScoreFromURL = searchParams.get('min_score');
    const maxScoreFromURL = searchParams.get('max_score');

    if (pageFromURL) {
      setCurrentPage(parseInt(pageFromURL, 10));
    }
    if (searchFromURL) {
      setSearchTerm(searchFromURL);
      setCurrentSearchTerm(searchFromURL);
    }

    let initialFilter = {};
    if (categoryFromURL) {
      initialFilter.category = categoryFromURL;
    }
    if (genderFromURL) {
      initialFilter.gender = genderFromURL;
    }
    if (stateFromURL) {
      initialFilter.state = stateFromURL.map(value => ({ value, label: value }));
    }
    if (lgaFromURL) {
      initialFilter.lga = lgaFromURL.map(value => ({ value, label: value }));
    }
    if (courseFromURL) {
      initialFilter.course = courseFromURL.map(value => ({ value, label: value }));
    }
    if (minScoreFromURL) {
      initialFilter.min_score = minScoreFromURL;
    }
    if (maxScoreFromURL) {
      initialFilter.max_score = maxScoreFromURL;
    }

    if (Object.keys(initialFilter).length > 0) {
      setFilter(initialFilter);
    }
  }, [searchParams, setCurrentPage, setSearchTerm, setFilter]);

  useEffect(() => {
    if (metadata && metadata.page) {
      setCurrentPage(metadata.page);
    }
  }, [metadata]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    updateFilters(currentPage, filter, currentSearchTerm);
  }, [filter, currentSearchTerm, updateFilters, currentPage]);

  useEffect(() => {
    if (!isLoading && leaderboardData && leaderboardData.length > 0 && currentUserId) {
      const currentUserIndex = leaderboardData.findIndex(item => item.id === currentUserId);

      if (currentUserIndex !== -1) {
        let newData = [...leaderboardData];

        if (currentUserIndex !== 2) {
          const currentUser = newData.splice(currentUserIndex, 1)[0];
          newData.splice(2, 0, currentUser);
          setIsCurrentUserFixed(true);
        }

        setData(newData);
      } else {
        setData(leaderboardData);
        setIsCurrentUserFixed(false);
      }
    } else {
      setData(leaderboardData || []);
      setIsCurrentUserFixed(false);
    }
  }, [isLoading, leaderboardData, currentUserId]);

  const toggleUnavailableModal = () => {
    setIsFilterModalVisible(false);
    setIsUnavailableModalVisible(!isUnavailableModalVisible);
  };

  const toggleFilterModal = () => {
    setIsFilterModalVisible(!isFilterModalVisible);
  };

  const getFilterModalPosition = () => {
    if (filterIconRef.current) {
      const rect = filterIconRef.current.getBoundingClientRect();
      return {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      };
    }
    return { top: 0, left: 0 };
  };

  const totalPages = metadata ? Math.ceil(metadata.count / pageSize) : 0;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
  } = useTable(
    {
      columns: COLUMNS,
      data,
      initialState: { pageIndex: currentPage - 1, pageSize },
      manualPagination: true,
      pageCount: totalPages,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  }, [totalPages]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    setCurrentSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleReset = useCallback(() => {
    setFilter("");
    setSearchTerm("");
    setCurrentSearchTerm("");
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback((newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    setIsFilterModalVisible(false);
  }, []);

  return (
    <div>
      <div className="leaderboard-card">
        <div className="leaderboard-card-body">
          <div className="leaderboard-header">
            <div className="filter-controls">
              <button
                className={`filter-btn ${isDropdownVisible ? 'active' : ''}`}
                onClick={toggleUnavailableModal}
                ref={gridIconRef}
              >
                <GridViewIcon />
              </button>
              <button
                className={`filter-btn ${isUnavailableModalVisible ? 'active' : ''}`}
                onClick={toggleUnavailableModal}
                ref={chartIconRef}
              >
                <ChartViewIcon />
              </button>
            </div>

            <div className="search-wrapper">
              <div className="search-icon">
                <SearchIcon />
              </div>
              <input
                type="text"
                className="search-input"
                placeholder="Search by name, Fellow ID, or course"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>

            <div className="filter-controls">
              <button
                className={`filter-btn ${isFilterModalVisible ? 'active' : ''}`}
                onClick={toggleFilterModal}
                ref={filterIconRef}
              >
                <FilterIcon />
              </button>
              <button
                className={`filter-btn ${isUnavailableModalVisible ? 'active' : ''}`}
                onClick={toggleUnavailableModal}
                ref={filterIconRef}
              >
                <CalendarIcon />
              </button>
            </div>
          </div>

          <LeaderboardFilterModal
            isVisible={isFilterModalVisible}
            setIsFilterModalVisible={setIsFilterModalVisible}
            position={getFilterModalPosition()}
            setFilter={handleFilterChange}
            setFilterString={setFilterString}
            setActiveRecord={setActiveRecord}
          />
          <FeatureUnavailableModal
            isVisible={isUnavailableModalVisible}
            setIsVisible={setIsUnavailableModalVisible}
          />

          {isLoading ? (
            <div className="loading-container">
              <FadeLoader color="#36d7b7" />
            </div>
          ) : (
            <div>
              {data?.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table
                      {...getTableProps()}
                      className="leaderboard-table"
                    >
                      <thead>
                        {headerGroups.map((headerGroup, idx) => (
                          <tr {...headerGroup.getHeaderGroupProps()} key={idx}>
                            {headerGroup.headers.map((column, colIdx) => (
                              <th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                className="leaderboard-header-cell"
                                key={colIdx}
                              >
                                {column.render("Header")}
                                {isCurrentUserFixed && currentPage === 1 && (
                                  <span className="visually-hidden">
                                    (Current user position is fixed)
                                  </span>
                                )}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {page.map((row, idx) => {
                          prepareRow(row);
                          const isCurrentUser = row.original.id === currentUserId;
                          return (
                            <tr
                              {...row.getRowProps()}
                              className={`leaderboard-row ${isCurrentUser ? 'current-user-row' : ''}`}
                              key={idx}
                            >
                              {row.cells.map((cell, cellIdx) => (
                                <td
                                  {...cell.getCellProps()}
                                  className="leaderboard-cell"
                                  key={cellIdx}
                                >
                                  {cell.render("Cell")}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    <div className="pagination-container">
                      {totalPages > 1 && (
                        <>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                          >
                            &laquo;
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            &lt;
                          </button>

                          <button
                            className={`pagination-btn ${currentPage === 1 ? 'pagination-btn-active' : ''}`}
                            onClick={() => handlePageChange(1)}
                          >
                            1
                          </button>

                          {totalPages > 1 && (
                            <>
                              {currentPage > 4 && <span className="pagination-ellipsis">...</span>}

                              {[...Array(Math.min(5, Math.max(0, totalPages - 2)))].map((_, i) => {
                                let page;
                                if (currentPage < 4) {
                                  page = i + 2;
                                } else if (currentPage > totalPages - 3) {
                                  page = totalPages - 5 + i;
                                } else {
                                  page = currentPage - 2 + i;
                                }
                                if (page > 1 && page < totalPages) {
                                  return (
                                    <button
                                      key={page}
                                      className={`pagination-btn ${currentPage === page ? 'pagination-btn-active' : ''}`}
                                      onClick={() => handlePageChange(page)}
                                    >
                                      {page}
                                    </button>
                                  );
                                }
                                return null;
                              })}

                              {currentPage < totalPages - 3 && totalPages > 5 && (
                                <span className="pagination-ellipsis">...</span>
                              )}

                              {totalPages > 1 && (
                                <button
                                  className={`pagination-btn ${currentPage === totalPages ? 'pagination-btn-active' : ''}`}
                                  onClick={() => handlePageChange(totalPages)}
                                >
                                  {totalPages}
                                </button>
                              )}
                            </>
                          )}

                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            &gt;
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            &raquo;
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <BagIcon />
                  <p className="empty-state-message">
                    No leaderboard data available yet.
                  </p>
                  <button className="reset-filter table-reset" onClick={handleReset}>
                    <ResetIcon />
                    Reset Filter
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicLeaderBoardTable;